import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AssuranceSort, AssuranceVideo, SafetyVideoCategories, Sort } from "@unbox/api";
import { ASSURANCE_VIDEOS_NEGATABLE_FILTERS } from "@unbox/api";
import { useCallback, useMemo } from "react";
import { handleError } from "@/api/error";
import { type Pagination, toQuery } from "@/api/pagination";
import type { ChunkResponse } from "@/hooks/use-progressive-fetch";
import { toNotQuery } from "@/providers/filter-registry";
import { client, getScopedMerchantClient, useScopedMerchantClient } from "../scoped-merchant-client";

type AssuranceVideosFilters = {
  search: string;
  publishDateStart?: string;
  publishDateEnd?: string;
  flaggedDateStart?: string;
  flaggedDateEnd?: string;
  riskId?: string[];
  riskTier?: string[];
  actionTaken?: string[];
  creatorType?: "affiliate" | "brand";
  creatorIds?: string[];
  hooks?: string[];
  productIds?: string[];
  tactics?: string[];
  sellingPoints?: string[];
  videoStyles?: string[];
  videoLanguages?: string[];
  campaignIds?: string[];
  videoRemovalStatus?: "live" | "removed";
  minViews?: number;
  maxViews?: number;
  not?: string[];
};

type AssuranceVideosParams = AssuranceVideosFilters & {
  category: SafetyVideoCategories;
  sort: Sort<AssuranceSort>;
};

const buildAssuranceVideosApiQuery = (params: AssuranceVideosParams) => ({
  ...params.sort,
  category: params.category,
  search: params.search,
  publishDateStart: params.publishDateStart,
  publishDateEnd: params.publishDateEnd,
  flaggedDateStart: params.flaggedDateStart,
  flaggedDateEnd: params.flaggedDateEnd,
  riskId: params.riskId,
  actionTaken: params.actionTaken,
  riskTier: params.riskTier,
  creatorType: params.creatorType,
  creatorIds: params.creatorIds,
  hooks: params.hooks,
  productIds: params.productIds,
  tactics: params.tactics,
  sellingPoints: params.sellingPoints,
  videoStyles: params.videoStyles,
  videoRemovalStatus: params.videoRemovalStatus,
  videoLanguages: params.videoLanguages,
  campaignIds: params.campaignIds,
  minViews: params.minViews?.toString(),
  maxViews: params.maxViews?.toString(),
  not: toNotQuery(params.not, ASSURANCE_VIDEOS_NEGATABLE_FILTERS),
});

// IMPORTANT: This query key is shared between `useAssuranceVideos` and
// `useAssuranceVideoRowsFetcher` so that `queryClient.fetchQuery` deduplicates
// against any already-mounted `useAssuranceVideos` query.
// Update both call sites together when filters change.
const assuranceVideosKey = (
  merchant: string,
  workspaceId: string | null,
  params: AssuranceVideosParams,
  pagination: Pagination,
) =>
  [
    "assurance-video",
    merchant,
    workspaceId,
    params.category,
    params.search,
    pagination.limit,
    pagination.offset,
    params.sort.column,
    params.sort.direction,
    params.publishDateStart,
    params.publishDateEnd,
    params.riskId?.toString(),
    params.riskTier?.toString(),
    params.actionTaken?.toString(),
    params.creatorType,
    params.creatorIds?.toString(),
    params.hooks?.toString(),
    params.productIds?.toString(),
    params.tactics?.toString(),
    params.sellingPoints?.toString(),
    params.videoStyles?.toString(),
    params.videoRemovalStatus,
    params.videoLanguages?.toString(),
    params.campaignIds?.toString(),
    params.minViews,
    params.maxViews,
    params.not?.toString(),
  ] as const;

const fetchAssuranceVideos = async (
  merchant: string,
  workspaceId: string | null,
  params: AssuranceVideosParams,
  pagination: { limit: number; offset: number },
) => {
  const scope = getScopedMerchantClient(merchant, workspaceId);
  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.video.$get({
        param: scope.param,
        query: {
          ...toQuery(pagination),
          ...buildAssuranceVideosApiQuery(params),
        },
      })
    : await client.merchant[":merchantId"].assurance.video.$get({
        param: scope.param,
        query: {
          ...toQuery(pagination),
          ...buildAssuranceVideosApiQuery(params),
        },
      });
  if (!res.ok) throw await handleError(res);
  return await res.json();
};

export const useAssuranceVideos = ({ pagination, ...params }: AssuranceVideosParams & { pagination: Pagination }) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: assuranceVideosKey(scope.merchantId, scope.workspaceId, params, pagination),
    queryFn: () => fetchAssuranceVideos(scope.merchantId, scope.workspaceId, params, pagination),
    enabled: !!scope.merchantId,
  });
};

export const useAssuranceVideoRowsFetcher = (params: AssuranceVideosParams) => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useCallback(
    async (offset: number, limit: number): Promise<ChunkResponse<AssuranceVideo>> => {
      const pagination = { limit, offset };
      const data = await queryClient.fetchQuery({
        queryKey: [...assuranceVideosKey(scope.merchantId, scope.workspaceId, params, pagination)],
        queryFn: () => fetchAssuranceVideos(scope.merchantId, scope.workspaceId, params, pagination),
        staleTime: Number.POSITIVE_INFINITY,
      });
      return { items: data.videos, total: data.total };
    },
    [queryClient, scope, params],
  );
};

const PAGE_SIZE = 20;

/**
 * Lazy-loads assurance videos page-by-page (PAGE_SIZE at a time) so the full
 * dataset is reachable via scroll. Accepts the same filter set as
 * `useAssuranceVideos` — search, sorting and every filter (including min/max
 * views) run server-side, so they apply across all videos, not just the loaded
 * page. Each page reuses `fetchAssuranceVideos`, so its per-page cache entry is
 * shared with `useAssuranceVideos`/`useAssuranceVideoRowsFetcher`.
 */
export const useInfiniteAssuranceVideos = (params: AssuranceVideosParams) => {
  const scope = useScopedMerchantClient();

  const query = useInfiniteQuery({
    queryKey: [
      "assurance-video-infinite",
      scope.merchantId,
      scope.workspaceId,
      PAGE_SIZE,
      params.category,
      params.search,
      params.sort.column,
      params.sort.direction,
      params.publishDateStart,
      params.publishDateEnd,
      params.riskId?.toString(),
      params.riskTier?.toString(),
      params.actionTaken?.toString(),
      params.creatorType,
      params.creatorIds?.toString(),
      params.hooks?.toString(),
      params.productIds?.toString(),
      params.tactics?.toString(),
      params.sellingPoints?.toString(),
      params.videoStyles?.toString(),
      params.videoRemovalStatus,
      params.videoLanguages?.toString(),
      params.campaignIds?.toString(),
      params.minViews,
      params.maxViews,
      params.not?.toString(),
    ],
    queryFn: ({ pageParam = 0 }) =>
      fetchAssuranceVideos(scope.merchantId, scope.workspaceId, params, { offset: pageParam, limit: PAGE_SIZE }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.videos.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: !!scope.merchantId,
  });

  // Offset pagination can hand back a videoId that was already loaded if the underlying
  // ordering shifts between page fetches. Duplicate keys put react-aria's table collection
  // into an infinite sibling loop (RangeError: Invalid array length), so dedupe here.
  const videos = useMemo(() => {
    const all = query.data?.pages.flatMap((p) => p.videos) ?? [];
    const seen = new Set<string>();
    const deduped: typeof all = [];
    for (const video of all) {
      if (seen.has(video.videoId)) continue;
      seen.add(video.videoId);
      deduped.push(video);
    }
    return deduped;
  }, [query.data]);
  const total = query.data?.pages[0]?.total ?? 0;

  return { ...query, videos, total };
};

const CHUNK_SIZE = 200;
export const useAssuranceVideosDownload = () => {
  const scope = useScopedMerchantClient();

  return useMutation<AssuranceVideo[], void, { total: number } & AssuranceVideosParams>({
    mutationFn: async ({ total, ...params }) => {
      const chunks = [...Array(Math.ceil(total / CHUNK_SIZE)).keys()];

      const requests = chunks.map((page) =>
        fetchAssuranceVideos(scope.merchantId, scope.workspaceId, params, {
          limit: CHUNK_SIZE,
          offset: CHUNK_SIZE * page,
        }),
      );

      try {
        const response = await Promise.all(requests);
        return response.flatMap((res) => res.videos);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
  });
};
